/**
 * Role Management Tab - Extracted from admin.tsx per ADR-039: Component Size Strategy
 * This component handles role listing, creation, editing, and deletion
 */

import type { RoleType } from "@/client/types.gen"
import type { RoleResponse } from "@/types/models"
import {
  Badge,
  Box,
  Card,
  Flex,
  Heading,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useState } from "react"
import { FiEdit, FiPlus, FiTrash } from "react-icons/fi"

import { Button } from "@/components/atoms/Button"
import { toaster } from "@/components/infrastructure/toaster"
import {
  useCreateRole,
  useDeleteRole,
  useRoles,
  useUpdateRole,
} from "@/hooks/api/useRoles"
import { useRBACPermissions } from "@/hooks/usePermissions"
import { getErrorMessage } from "@/utils/typeGuards"
import { CreateRoleModal } from "../modals/CreateRoleModal"
import { EditRoleModal } from "../modals/EditRoleModal"

export function RoleManagementTab() {
  const { data: roles = [], isLoading } = useRoles(true)
  const { permissions = [], isLoading: permissionsLoading } =
    useRBACPermissions()
  const createRoleMutation = useCreateRole()
  const updateRoleMutation = useUpdateRole()
  const deleteRoleMutation = useDeleteRole()
  const [open, setOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<RoleResponse | null>(null)

  const handleDeleteRole = async (role: RoleResponse) => {
    // Only allow deletion of custom roles
    if (role.is_system_role) {
      toaster.create({
        title: "Cannot Delete System Role",
        description: "System roles cannot be deleted",
        type: "warning",
      })
      return
    }

    if (
      window.confirm(
        `Are you sure you want to delete the role "${role.display_name || role.name}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteRoleMutation.mutateAsync(role.id)
        toaster.create({
          title: "Role Deleted",
          description: `Role "${role.display_name || role.name}" has been deleted`,
          type: "success",
        })
      } catch (error) {
        const errorMessage = getErrorMessage(error, "Failed to delete role")
        toaster.create({
          title: "Error Deleting Role",
          description: errorMessage,
          type: "error",
        })
      }
    }
  }

  if (isLoading || permissionsLoading) {
    return (
      <Text p={4} textAlign="center" color="gray.500">
        Loading roles...
      </Text>
    )
  }

  return (
    <Card.Root>
      <Card.Header>
        <Flex>
          <Stack align="start" gap={1}>
            <Heading size="md">System Roles</Heading>
            <Text fontSize="sm" color="gray.600">
              Manage system roles and their associated permissions
            </Text>
          </Stack>
          <Spacer />
          <Button onClick={() => setOpen(true)} gap={2}>
            <FiPlus />
            Create Role
          </Button>
        </Flex>
      </Card.Header>
      <Card.Body>
        <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
          {roles.map((role) => (
            <Card.Root key={role.id} variant="outline">
              <Card.Body>
                <Stack align="start" gap={3}>
                  <Flex>
                    <Badge
                      colorScheme={role.is_system_role ? "blue" : "purple"}
                      size="lg"
                    >
                      {role.display_name || role.name}
                    </Badge>
                    <Badge
                      variant="outline"
                      colorScheme={role.is_active ? "green" : "gray"}
                    >
                      {role.is_active ? "Active" : "Inactive"}
                    </Badge>
                    {role.is_system_role && (
                      <Badge variant="solid" colorScheme="orange">
                        System
                      </Badge>
                    )}
                  </Flex>
                  <Text fontSize="sm" color="gray.600">
                    {role.description}
                  </Text>
                  <Box>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="gray.500"
                      mb={1}
                    >
                      PERMISSIONS ({role.permissions?.length || 0})
                    </Text>
                    <Flex wrap="wrap">
                      {role.permissions?.slice(0, 5).map((permission) => (
                        <Badge key={permission.id} variant="outline" size="sm">
                          {permission.name.split(":")[1] || permission.name}
                        </Badge>
                      ))}
                      {(role.permissions?.length || 0) > 5 && (
                        <Badge variant="outline" size="sm">
                          +{(role.permissions?.length || 0) - 5} more
                        </Badge>
                      )}
                    </Flex>
                  </Box>

                  {/* Action buttons for non-system roles */}
                  {!role.is_system_role && (
                    <Flex pt={2} gap={2}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingRole(role)}
                        gap={1}
                      >
                        <FiEdit />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        colorScheme="red"
                        onClick={() => handleDeleteRole(role)}
                        disabled={deleteRoleMutation.isPending}
                        gap={1}
                      >
                        <FiTrash />
                        Delete
                      </Button>
                    </Flex>
                  )}
                </Stack>
              </Card.Body>
            </Card.Root>
          ))}
        </SimpleGrid>
      </Card.Body>

      <CreateRoleModal
        isOpen={open}
        onClose={() => setOpen(false)}
        permissions={permissions || []}
        onCreateRole={(data) =>
          createRoleMutation.mutateAsync({
            ...data,
            role_type: data.role_type as RoleType,
          })
        }
      />

      <EditRoleModal
        isOpen={!!editingRole}
        onClose={() => setEditingRole(null)}
        role={editingRole}
        permissions={permissions || []}
        onUpdateRole={updateRoleMutation.mutateAsync}
      />
    </Card.Root>
  )
}
